import { ExplorerTracking } from "@/lib/tracking/explorer";
import { useMemo } from "react";
import { useOpportunities } from "../../useOpportunity";
import { useUsers } from "@/helpers/hooks";
import { isUserDeleted } from "@/helpers/utils";
import { useAppState } from "@/modules/app/useAppContext";
import { Combobox } from "@altimateai/lego";

interface UserOption {
  label: string;
  value: string;
}

interface OwnerAssignerDropdownProps {
  opportunity_id?: string;
  assignedOwner?: string | null;
  setAssignedOwner: (status: string) => void;
  disableUpdateOnChange?: boolean;
  startDate?: string;
  endDate?: string;
  /** Optional pre-computed user list to avoid re-fetching */
  userOptions?: UserOption[];
}

interface HandleOwnerChangeParams {
  e: string | unknown;
  opportunity_id: string | undefined;
}

const OwnerAssignerDropdown = ({
  opportunity_id,
  assignedOwner,
  setAssignedOwner = () => {},
  disableUpdateOnChange = false,
  startDate,
  endDate,
  userOptions,
}: OwnerAssignerDropdownProps) => {
  const { mutation } = useOpportunities();
  const { mutate } = mutation;
  const { loggedinUser: user } = useAppState();
  // Always fetch users (useUsers doesn't support skip; results ignored when userOptions provided)
  const { users } = useUsers();

  // Build user list from props or from fetched users
  const userList = useMemo(() => {
    // If userOptions provided, use it directly
    if (userOptions) {
      return userOptions;
    }
    // Fallback: build from fetched users
    if (!users) return [{ label: "Unassigned", value: "-1" }];
    return [
      { label: "Unassigned", value: "-1" },
      ...users
        .filter(u => u.first_name || u.last_name)
        .filter(u => !isUserDeleted(u) || u.id.toString() === assignedOwner)
        .map(u => {
          const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ");
          return {
            label: isUserDeleted(u) ? `${fullName} (deleted)` : fullName,
            value: u.id.toString(),
          };
        }),
    ];
  }, [userOptions, users, assignedOwner]);

  // Build valueLabels to prevent flicker when displaying selected value
  const valueLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    userList.forEach(opt => {
      labels[opt.value] = opt.label;
    });
    return labels;
  }, [userList]);

  const handleOwnerChange = ({ e, opportunity_id }: HandleOwnerChangeParams) => {
    ExplorerTracking.opportunityOwnerAssigned(opportunity_id ?? "", e as string);
    mutate({
      changes: [
        {
          opportunity_id: opportunity_id ?? "",
          owner: e as string,
          status: undefined,
        },
      ],
      user_id: user?.id,
      start_date: startDate ?? "",
      end_date: endDate ?? "",
    });
  };

  return (
    <Combobox
      id={`state-assigner`}
      buttonProps={{ size: "sm", className: "!al-w-full" }}
      onChange={e => {
        setAssignedOwner(e as string);
        if (!disableUpdateOnChange) {
          handleOwnerChange({ e, opportunity_id });
        }
      }}
      value={assignedOwner || "-1"}
      options={userList}
      valueLabels={valueLabels}
      placeholder="Unassigned"
    />
  );
};

export default OwnerAssignerDropdown;
